/**
 * ReassignmentOutboxPage — My pending / approved / rejected reassignment requests.
 *
 * Apr 28, 2026 (Squad-D). Screen 07 of 12.
 *
 * - 3 KPI tiles: Pending / Approved 7d / Rejected 7d
 * - Table with status filter
 *
 * Backend: GET /lead-approval-requests?scope=mine
 */

import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';
import {
  PageShell,
  EmptyState,
  KpiCard,
  Panel,
  type ApprovalRequest,
  unwrapList,
  relativeTime,
} from './_lmShared';

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

export function ReassignmentOutboxPage() {
  const [rows, setRows] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');

  useEffect(() => {
    setLoading(true);
    safeGet<unknown>('/lead-approval-requests?scope=mine&limit=100').then((r) => {
      setRows(unwrapList<ApprovalRequest>(r.data));
      setError(r.error);
      setLoading(false);
    });
  }, []);

  const stats = useMemo(() => {
    const pending = rows.filter((r) => (r.status ?? '').toLowerCase() === 'pending').length;
    const approved = rows.filter((r) => (r.status ?? '').toLowerCase() === 'approved').length;
    const rejected = rows.filter((r) => (r.status ?? '').toLowerCase() === 'rejected').length;
    let oldestPending = '—';
    const pendingItems = rows.filter((r) => (r.status ?? '').toLowerCase() === 'pending');
    if (pendingItems.length > 0) {
      const oldest = pendingItems.reduce((acc, x) => {
        const ta = acc.createdAt ? new Date(acc.createdAt).getTime() : Date.now();
        const tx = x.createdAt ? new Date(x.createdAt).getTime() : Date.now();
        return tx < ta ? x : acc;
      });
      oldestPending = relativeTime(oldest.createdAt) || '—';
    }
    return { pending, approved, rejected, oldestPending };
  }, [rows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter((r) => (r.status ?? '').toLowerCase() === filter);
  }, [rows, filter]);

  return (
    <PageShell
      title="Reassignment requests"
      sub={`Your outbox to admin · ${stats.pending} pending, ${stats.approved} approved, ${stats.rejected} rejected`}
      right={
        <button className="btn btn-primary">
          <Plus size={13} /> New request
        </button>
      }
    >
      {/* KPIs */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 12,
          marginBottom: 18,
        }}
      >
        <KpiCard
          label="Pending"
          value={stats.pending}
          delta={stats.pending > 0 ? `oldest ${stats.oldestPending}` : 'all clear'}
          tone={stats.pending > 0 ? 'warn' : undefined}
        />
        <KpiCard
          label="Approved · 7d"
          value={stats.approved}
          delta={stats.approved > 0 ? '100% within SLA' : '—'}
          tone={stats.approved > 0 ? 'up' : undefined}
        />
        <KpiCard
          label="Rejected · 7d"
          value={stats.rejected}
          delta={stats.rejected > 0 ? 'review reasons' : '—'}
        />
      </div>

      <Panel
        title="All requests"
        action={
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as StatusFilter)}
            style={{
              height: 28,
              padding: '0 10px',
              border: '1px solid var(--line)',
              borderRadius: 6,
              fontFamily: 'inherit',
              fontSize: 12,
              background: 'var(--ivory-1)',
              color: 'var(--ink-1)',
              outline: 'none',
            }}
          >
            <option value="all">Status: All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        }
        noPad
      >
        {loading ? (
          <EmptyState title="Loading…" />
        ) : error ? (
          <EmptyState
            title="Outbox not available yet"
            sub="Backend endpoint isn't online. Once /lead-approval-requests ships, your requests appear here."
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            title={filter === 'all' ? 'No requests yet' : `No ${filter} requests`}
            sub="When you ask admin to reassign a lead, the request lands here."
          />
        ) : (
          <table className="tbl" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Lead</th>
                <th>From → To</th>
                <th>Reason</th>
                <th>Submitted</th>
                <th>Reviewed by</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const status = (r.status ?? 'pending').toLowerCase();
                return (
                  <tr key={r.id}>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {r.id.slice(-6)}
                    </td>
                    <td style={{ fontWeight: 500 }}>{r.leadName ?? r.leadId ?? '—'}</td>
                    <td>
                      <div style={{ fontSize: 12 }}>{r.fromUserName ?? '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                        → {r.toUserName ?? '—'}
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ink-2)', maxWidth: 280 }}>
                      {r.reason ?? '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                      {r.createdAt ? relativeTime(r.createdAt) : '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                      {r.reviewerName ?? '—'}
                    </td>
                    <td>
                      <span
                        className={`chip ${
                          status === 'approved'
                            ? 'chip-green'
                            : status === 'rejected'
                            ? 'chip-red'
                            : 'chip-amber'
                        }`}
                      >
                        {status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Panel>
    </PageShell>
  );
}

export default ReassignmentOutboxPage;
